import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("polling"); // polling | paid | timeout | error
  const attemptsRef = useRef(0);
  const { refresh } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!sessionId) { setStatus("error"); return; }
    let stop = false;
    const poll = async () => {
      if (stop) return;
      attemptsRef.current += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setStatus("paid");
          await refresh();
          return;
        }
        if (attemptsRef.current >= 15) {
          setStatus("timeout");
          return;
        }
        setTimeout(poll, 2000);
      } catch (e) {
        console.error(errMsg(e));
        if (attemptsRef.current >= 15) setStatus("timeout");
        else setTimeout(poll, 2000);
      }
    };
    poll();
    return () => { stop = true; };
  }, [sessionId, refresh]);

  return (
    <div className="min-h-[60vh] flex items-center justify-center p-4" data-testid="payment-success-page">
      <Card className="max-w-md w-full">
        <CardContent className="pt-8 pb-6 text-center space-y-4">
          {status === "polling" && (
            <>
              <Loader2 className="w-16 h-16 text-blue-600 mx-auto animate-spin" />
              <h1 className="font-display text-2xl font-bold">Confirmando tu pago...</h1>
              <p className="text-slate-500">Un momento, estamos activando tu suscripción.</p>
            </>
          )}
          {status === "paid" && (
            <>
              <CheckCircle2 className="w-16 h-16 text-emerald-600 mx-auto" />
              <h1 className="font-display text-2xl font-bold text-emerald-800">¡Pago confirmado!</h1>
              <p className="text-slate-600">Tu suscripción está activa. Todas las funciones están desbloqueadas.</p>
              <Button onClick={() => navigate("/")} className="h-11 w-full bg-blue-700 hover:bg-blue-800" data-testid="go-home-btn">
                Ir al inicio
              </Button>
            </>
          )}
          {status === "timeout" && (
            <>
              <Loader2 className="w-16 h-16 text-amber-600 mx-auto" />
              <h1 className="font-display text-2xl font-bold text-amber-800">Estamos procesando tu pago</h1>
              <p className="text-slate-600">A veces tarda unos minutos. Refresca la página o vuelve al inicio.</p>
              <Button onClick={() => navigate("/")} className="h-11 w-full" variant="outline">Ir al inicio</Button>
            </>
          )}
          {status === "error" && (
            <>
              <XCircle className="w-16 h-16 text-red-600 mx-auto" />
              <h1 className="font-display text-2xl font-bold text-red-800">Faltan datos</h1>
              <p className="text-slate-600">No hemos recibido un identificador de sesión válido.</p>
              <Button onClick={() => navigate("/")} className="h-11 w-full" variant="outline">Ir al inicio</Button>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
